import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, branding as brandApi, navLinks, banners, categories, products, features, stats, faqs, pricing, sizeCharts, uploadImage, pageSettings as pageSettingsApi, orders as ordersApi, customers } from '../lib/api';
import { useSite } from '../lib/siteContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Toaster, toast } from 'sonner';
import { LogOut, Save, Plus, Trash2, Loader2, ExternalLink, Upload, X, Copy, RefreshCw, Package, ShoppingBag, User } from 'lucide-react';

const ORDER_STATUS_META = {
  upcoming: { label: 'Upcoming', chip: 'bg-neutral-100 text-neutral-700 border-neutral-300' },
  pending: { label: 'Pending', chip: 'bg-amber-100 text-amber-800 border-amber-300' },
  packed: { label: 'Packed', chip: 'bg-blue-100 text-blue-800 border-blue-300' },
  ready_for_dispatch: { label: 'Ready for Dispatch', chip: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  completed: { label: 'Completed', chip: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  cancelled: { label: 'Cancelled', chip: 'bg-red-100 text-red-700 border-red-300' },
};
const ORDER_STATUS_ORDER = ['upcoming', 'pending', 'packed', 'ready_for_dispatch', 'completed', 'cancelled'];

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const OrdersManager = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setList(await ordersApi.list()); } catch { toast.error('Orders load failed'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const counts = ORDER_STATUS_ORDER.reduce((a, s) => { a[s] = list.filter((o) => o.status === s).length; return a; }, {});

  const filtered = list
    .filter((o) => filter === 'all' || o.status === filter)
    .filter((o) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return [o.buyer_name, o.buyer_phone, o.sales_rep, o.product_name].some((v) => (v || '').toLowerCase().includes(q));
    });

  const changeStatus = async (id, status, tracking) => {
    setBusy(id);
    try { await ordersApi.setStatus(id, status, tracking); await load(); toast.success('Order updated'); }
    catch { toast.error('Update failed'); } finally { setBusy(null); }
  };
  const del = async (id) => {
    if (!confirm('Delete this order permanently?')) return;
    setBusy(id);
    try { await ordersApi.remove(id); await load(); toast.success('Order deleted'); }
    catch { toast.error('Delete failed'); } finally { setBusy(null); }
  };

  return (
    <SectionCard
      title={`Orders (${list.length})`}
      action={<Button data-testid="orders-refresh-button" size="sm" variant="outline" onClick={load} disabled={loading}><RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>}
    >
      <div className="flex flex-wrap gap-1.5 mb-3">
        <button data-testid="orders-filter-all" onClick={() => setFilter('all')} className={`text-[12px] px-2.5 py-1 rounded-full border font-medium ${filter === 'all' ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white text-neutral-700 border-neutral-200'}`}>
          All ({list.length})
        </button>
        {ORDER_STATUS_ORDER.map((s) => (
          <button key={s} data-testid={`orders-filter-${s}`} onClick={() => setFilter(s)} className={`text-[12px] px-2.5 py-1 rounded-full border font-medium ${filter === s ? 'bg-neutral-900 text-white border-neutral-900' : ORDER_STATUS_META[s].chip}`}>
            {ORDER_STATUS_META[s].label} ({counts[s]})
          </button>
        ))}
      </div>

      <Input data-testid="orders-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by buyer name, phone, sales rep or product…" className="mb-3 h-9 text-[13px]" />

      {loading ? (
        <div className="py-10 text-center text-neutral-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-10 text-center text-[13px] text-neutral-500"><ShoppingBag className="w-8 h-8 mx-auto mb-2 text-neutral-300" />No orders here yet.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((o) => (
            <div key={o.id} data-testid={`order-card-${o.id}`} className="border border-neutral-200 rounded-lg p-3 bg-white">
              <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[14px] text-neutral-900">{o.product_name || 'Order'}</span>
                    <span data-testid={`order-status-badge-${o.id}`} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${ORDER_STATUS_META[o.status]?.chip || ''}`}>
                      {ORDER_STATUS_META[o.status]?.label || o.status}
                    </span>
                  </div>
                  <div className="text-[11px] text-neutral-500 mt-0.5">{fmtDate(o.created_at)} · {o.total_qty} pcs · ₹{(o.grand_total || 0).toLocaleString('en-IN')}</div>
                </div>
                <div className="flex items-center gap-1.5">
                  <select
                    data-testid={`order-status-select-${o.id}`}
                    value={o.status}
                    disabled={busy === o.id}
                    onChange={(e) => changeStatus(o.id, e.target.value)}
                    className="h-8 px-2 rounded border border-neutral-300 text-[12px] bg-white"
                  >
                    {ORDER_STATUS_ORDER.map((s) => <option key={s} value={s}>{ORDER_STATUS_META[s].label}</option>)}
                  </select>
                  <Button data-testid={`order-delete-${o.id}`} size="sm" variant="outline" onClick={() => del(o.id)} disabled={busy === o.id} className="text-red-600 h-8 px-2"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>

              <div className="flex items-center gap-1.5 mb-2">
                <span className="text-[11px] text-neutral-500">Tracking #:</span>
                <Input
                  data-testid={`order-tracking-input-${o.id}`}
                  defaultValue={o.tracking_number || ''}
                  onBlur={(e) => { if (e.target.value !== (o.tracking_number || '')) changeStatus(o.id, o.status, e.target.value); }}
                  placeholder="courier tracking no."
                  className="h-7 text-[12px] max-w-[220px]"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-x-4 gap-y-1 text-[12px] text-neutral-700 mb-2">
                <div><span className="text-neutral-500">Buyer:</span> <b>{o.buyer_name || '—'}</b></div>
                <div><span className="text-neutral-500">Phone:</span> <a href={`tel:${o.buyer_phone}`} className="text-blue-700">{o.buyer_phone || '—'}</a></div>
                <div><span className="text-neutral-500">Email:</span> {o.buyer_email || '—'}</div>
                <div><span className="text-neutral-500">GST:</span> {o.buyer_gst || '—'}</div>
                <div className="md:col-span-2"><span className="text-neutral-500">Delivery:</span> {o.buyer_address || '—'}</div>
                <div className="flex items-center gap-1"><User className="w-3 h-3 text-orange-500" /><span className="text-neutral-500">Sales rep:</span> <b className="text-orange-700">{o.sales_rep || 'self'}</b></div>
                <div><span className="text-neutral-500">Payment:</span> {o.payment_method || '—'}{o.payment_id ? ` · ${o.payment_id}` : ''}</div>
              </div>

              {(o.items || []).length > 0 && (
                <div className="rounded border border-neutral-100 bg-neutral-50 p-2 mb-1">
                  <div className="text-[11px] font-semibold text-neutral-600 mb-1 flex items-center gap-1"><Package className="w-3 h-3" /> Items</div>
                  <div className="flex flex-wrap gap-1.5">
                    {o.items.map((it, idx) => (
                      <span key={idx} className="text-[11px] bg-white border border-neutral-200 rounded px-1.5 py-0.5">{it.color} / {it.size} — <b>{it.qty}</b></span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-x-4 text-[11px] text-neutral-500">
                <span>Subtotal: ₹{(o.subtotal || 0).toLocaleString('en-IN')}</span>
                <span>GST ({o.gst_rate || 5}%): ₹{(o.gst_amount || 0).toLocaleString('en-IN')}</span>
                <span className="font-semibold text-neutral-800">Grand Total: ₹{(o.grand_total || 0).toLocaleString('en-IN')}</span>
                {o.notes ? <span className="w-full">Note: {o.notes}</span> : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
};

const SectionCard = ({ title, children, action }) => (
  <div className="bg-white rounded-lg border border-neutral-200 p-4 mb-4">
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-semibold text-neutral-900">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

const ImageUploader = ({ value, onChange, onRemove }) => {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      onChange(url);
      toast.success('Uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };
  return (
    <div className="flex items-center gap-2">
      {value ? (
        <div className="relative w-20 h-20 rounded border border-neutral-200 overflow-hidden">
          <img src={value} alt="" className="w-full h-full object-cover" />
          {onRemove && (
            <button type="button" onClick={onRemove} className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full w-5 h-5 grid place-items-center">
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      ) : null}
      <label className="cursor-pointer inline-flex items-center gap-1 px-2.5 py-1.5 text-[12px] border border-dashed border-neutral-300 rounded hover:border-orange-500 hover:bg-orange-50">
        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
        Upload
        <input type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </label>
    </div>
  );
};

const BrandingEditor = ({ data, onSave }) => {
  const [form, setForm] = useState(data);
  const [saving, setSaving] = useState(false);
  const [uploadingQR, setUploadingQR] = useState(false);
  useEffect(() => setForm(data), [data]);
  const save = async () => {
    setSaving(true);
    try { await brandApi.update(form); toast.success('Saved'); onSave(); } catch (e) { toast.error('Failed'); }
    finally { setSaving(false); }
  };
  const setField = (k, v) => setForm({ ...form, [k]: v });

  const uploadQR = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingQR(true);
    try {
      const url = await uploadImage(file);
      setField('upi_qr_url', url);
      toast.success('QR uploaded — click Save');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingQR(false); e.target.value = ''; }
  };

  return (
    <SectionCard title="Site Branding & Contact" action={<Button size="sm" onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600"><Save className="w-3.5 h-3.5 mr-1" />Save</Button>}>
      <div className="grid md:grid-cols-2 gap-3">
        <div><Label>Brand name</Label><Input value={form.name || ''} onChange={(e) => setField('name', e.target.value)} /></div>
        <div><Label>Display name (subtitle)</Label><Input value={form.display_name || ''} onChange={(e) => setField('display_name', e.target.value)} /></div>
        <div className="md:col-span-2"><Label>Tagline (top bar)</Label><Input value={form.tagline || ''} onChange={(e) => setField('tagline', e.target.value)} /></div>
        <div><Label>Sold last month</Label><Input value={form.sold_last_month || ''} onChange={(e) => setField('sold_last_month', e.target.value)} /></div>
        <div><Label>Month/Year label</Label><Input value={form.month_year || ''} onChange={(e) => setField('month_year', e.target.value)} /></div>
        <div className="md:col-span-2"><Label>Hero quote (features section)</Label><Textarea rows={3} value={form.hero_quote || ''} onChange={(e) => setField('hero_quote', e.target.value)} /></div>
        <div><Label>Contact phone 1</Label><Input value={form.contact_phone || ''} onChange={(e) => setField('contact_phone', e.target.value)} /></div>
        <div><Label>Contact phone 2</Label><Input value={form.contact_phone_2 || ''} onChange={(e) => setField('contact_phone_2', e.target.value)} /></div>
        <div><Label>Contact email</Label><Input value={form.contact_email || ''} onChange={(e) => setField('contact_email', e.target.value)} /></div>
        <div><Label>Contact address</Label><Input value={form.contact_address || ''} onChange={(e) => setField('contact_address', e.target.value)} /></div>
      </div>

      <div className="mt-4 pt-3 border-t border-neutral-200">
        <div className="text-[13px] font-semibold text-neutral-800 mb-2">Payment & Order Settings</div>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label>WhatsApp number (orders go here, digits only with country code)</Label>
            <Input value={form.whatsapp_number || ''} onChange={(e) => setField('whatsapp_number', e.target.value)} placeholder="919971890401" />
            <p className="text-[11px] text-neutral-500 mt-1">Example: 91 + phone number, no spaces or +.</p>
          </div>
          <div>
            <Label>UPI ID (optional)</Label>
            <Input value={form.upi_id || ''} onChange={(e) => setField('upi_id', e.target.value)} placeholder="yourname@paytm" />
          </div>
          <div>
            <Label>Razorpay Key ID (optional)</Label>
            <Input value={form.razorpay_key_id || ''} onChange={(e) => setField('razorpay_key_id', e.target.value)} placeholder="rzp_live_xxxx or rzp_test_xxxx" />
          </div>
          <div>
            <Label>Default "Sale by" code</Label>
            <Input value={form.default_sales_rep || ''} onChange={(e) => setField('default_sales_rep', e.target.value)} placeholder="self" />
          </div>
          <div>
            <Label>UPI QR Image</Label>
            <div className="flex items-center gap-3 mt-1">
              {form.upi_qr_url ? (
                <div className="relative w-24 h-24 rounded border border-neutral-200 overflow-hidden bg-white">
                  <img src={form.upi_qr_url} alt="QR" className="w-full h-full object-contain" />
                </div>
              ) : null}
              <label className="cursor-pointer inline-flex items-center gap-1 px-2.5 py-1.5 text-[12px] border border-dashed border-neutral-300 rounded hover:border-orange-500 hover:bg-orange-50">
                {uploadingQR ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {form.upi_qr_url ? 'Replace QR' : 'Upload QR'}
                <input type="file" accept="image/*" className="hidden" onChange={uploadQR} />
              </label>
              {form.upi_qr_url && (
                <button onClick={() => setField('upi_qr_url', '')} className="text-red-500 hover:text-red-700 text-[12px]">Remove</button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-neutral-200">
        <div className="text-[13px] font-semibold text-neutral-800 mb-2">Bank Transfer Details</div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Account Name</Label><Input value={form.bank_account_name || ''} onChange={(e) => setField('bank_account_name', e.target.value)} placeholder="RK TRADERS" /></div>
          <div><Label>Account Number</Label><Input value={form.bank_account_number || ''} onChange={(e) => setField('bank_account_number', e.target.value)} placeholder="3979000100165921" /></div>
          <div><Label>IFSC</Label><Input value={form.bank_ifsc || ''} onChange={(e) => setField('bank_ifsc', e.target.value)} placeholder="AUBL0002101" /></div>
          <div><Label>Bank Name</Label><Input value={form.bank_name || ''} onChange={(e) => setField('bank_name', e.target.value)} placeholder="AU Small Finance Bank" /></div>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-neutral-200">
        <div className="text-[13px] font-semibold text-neutral-800 mb-2">GST</div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>GST Rate (%)</Label>
            <Input type="number" value={form.gst_rate ?? 5} onChange={(e) => setField('gst_rate', parseFloat(e.target.value) || 0)} />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-[13px] cursor-pointer">
              <input type="checkbox" checked={form.gst_required !== false} onChange={(e) => setField('gst_required', e.target.checked)} className="accent-orange-500" />
              Charge GST on every order
            </label>
          </div>
        </div>
      </div>
    </SectionCard>
  );
};

const SimpleListEditor = ({ title, items, api, fields, refresh, defaultNew }) => {
  const [list, setList] = useState(items || []);
  const [busy, setBusy] = useState(null);
  useEffect(() => setList(items || []), [items]);

  const updateItem = (i, k, v) => {
    const next = [...list];
    next[i] = { ...next[i], [k]: v };
    setList(next);
  };

  const save = async (i) => {
    const item = list[i];
    setBusy(item.id);
    try {
      if (item.id) await api.update(item.id, item);
      else await api.create(item);
      toast.success('Saved'); await refresh();
    } catch { toast.error('Save failed'); } finally { setBusy(null); }
  };

  const del = async (i) => {
    const item = list[i];
    if (!confirm('Delete this item?')) return;
    if (item.id) { setBusy(item.id); try { await api.remove(item.id); await refresh(); toast.success('Deleted'); } catch { toast.error('Delete failed'); } finally { setBusy(null); } }
    else setList(list.filter((_, j) => j !== i));
  };

  const add = () => setList([...list, { ...defaultNew, order: list.length }]);

  return (
    <SectionCard title={title} action={<Button size="sm" onClick={add} variant="outline"><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>}>
      <div className="space-y-2">
        {list.map((item, i) => (
          <div key={item.id || `new-${i}`} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end bg-neutral-50 p-2 rounded border border-neutral-200">
            {fields.map((f) => (
              <div key={f.key} className={f.cls || 'md:col-span-4'}>
                <Label className="text-[11px]">{f.label}</Label>
                {f.type === 'textarea' ? (
                  <Textarea rows={2} value={item[f.key] || ''} onChange={(e) => updateItem(i, f.key, e.target.value)} />
                ) : f.type === 'number' ? (
                  <Input type="number" value={item[f.key] || 0} onChange={(e) => updateItem(i, f.key, parseInt(e.target.value) || 0)} />
                ) : f.type === 'select' ? (
                  <select data-testid={f.testId ? `${f.testId}-${i}` : undefined} value={item[f.key] || ''} onChange={(e) => updateItem(i, f.key, e.target.value)} className="w-full h-9 px-2 rounded border border-neutral-300 text-[13px] bg-white">
                    {(f.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <Input value={item[f.key] || ''} onChange={(e) => updateItem(i, f.key, e.target.value)} />
                )}
              </div>
            ))}
            <div className="md:col-span-2 flex gap-1">
              <Button size="sm" onClick={() => save(i)} disabled={busy === item.id} className="bg-orange-500 hover:bg-orange-600 flex-1">
                {busy === item.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              </Button>
              <Button size="sm" variant="outline" onClick={() => del(i)} className="text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="text-[13px] text-neutral-500 text-center py-4">No items. Click Add.</p>}
      </div>
    </SectionCard>
  );
};

const SizeChartsEditor = ({ list: chartsList, refresh }) => {
  const [list, setList] = useState(chartsList || []);
  const [busy, setBusy] = useState(null);
  useEffect(() => setList(chartsList || []), [chartsList]);

  const update = (i, k, v) => { const next = [...list]; next[i] = { ...next[i], [k]: v }; setList(next); };

  const updateRow = (i, ri, k, v) => {
    const next = [...list]; const rows = [...(next[i].rows || [])];
    rows[ri] = { ...rows[ri], [k]: v }; next[i] = { ...next[i], rows }; setList(next);
  };
  const addRow = (i) => { const next = [...list]; next[i] = { ...next[i], rows: [...(next[i].rows || []), { size: '', chest: '', length: '', shoulder: '' }] }; setList(next); };
  const removeRow = (i, ri) => { const next = [...list]; next[i] = { ...next[i], rows: next[i].rows.filter((_, x) => x !== ri) }; setList(next); };

  const save = async (i) => {
    const item = list[i]; setBusy(item.id || `new-${i}`);
    try {
      if (item.id) await sizeCharts.update(item.id, item);
      else await sizeCharts.create(item);
      toast.success('Saved'); await refresh();
    } catch { toast.error('Save failed'); } finally { setBusy(null); }
  };

  const del = async (i) => {
    const item = list[i]; if (!confirm('Delete this size chart?')) return;
    if (item.id) { setBusy(item.id); try { await sizeCharts.remove(item.id); await refresh(); toast.success('Deleted'); } catch { toast.error('Failed'); } finally { setBusy(null); } }
    else setList(list.filter((_, j) => j !== i));
  };

  const add = () => setList([...list, { name: 'New Size Chart', rows: [{ size: 'S', chest: '', length: '', shoulder: '' }], order: list.length }]);

  return (
    <SectionCard title="Size Charts" action={<Button size="sm" onClick={add} variant="outline"><Plus className="w-3.5 h-3.5 mr-1" />Add Size Chart</Button>}>
      <p className="text-[12px] text-neutral-500 mb-3">Create size charts here, then assign one to each product in the Products tab.</p>
      <div className="space-y-4">
        {list.map((sc, i) => (
          <div key={sc.id || `new-${i}`} data-testid={`size-chart-${i}-editor`} className="border border-neutral-200 rounded-lg p-3 bg-neutral-50">
            <div className="mb-2">
              <Label className="text-[11px]">Chart name</Label>
              <Input data-testid={`size-chart-${i}-name-input`} value={sc.name || ''} onChange={(e) => update(i, 'name', e.target.value)} placeholder="e.g. Oversize Tee Chart" />
            </div>
            <div className="mb-2">
              <div className="flex items-center justify-between mb-1">
                <Label className="text-[11px]">Rows (inches)</Label>
                <Button data-testid={`size-chart-${i}-add-row-button`} size="sm" variant="outline" onClick={() => addRow(i)} className="h-6 text-[11px]"><Plus className="w-3 h-3 mr-0.5" />Row</Button>
              </div>
              <div className="space-y-1.5">
                {(sc.rows || []).map((r, ri) => (
                  <div key={ri} data-testid={`size-chart-${i}-row-${ri}-editor`} className="grid grid-cols-[1fr,1fr,1fr,1fr,32px] gap-1.5 items-end bg-white border rounded p-1.5">
                    <div><Label className="text-[10px]">Size</Label><Input data-testid={`size-chart-${i}-row-${ri}-size-input`} value={r.size} onChange={(e) => updateRow(i, ri, 'size', e.target.value)} className="h-8 text-[12px]" /></div>
                    <div><Label className="text-[10px]">Chest</Label><Input data-testid={`size-chart-${i}-row-${ri}-chest-input`} value={r.chest} onChange={(e) => updateRow(i, ri, 'chest', e.target.value)} className="h-8 text-[12px]" placeholder='e.g. 20&quot;' /></div>
                    <div><Label className="text-[10px]">Length</Label><Input data-testid={`size-chart-${i}-row-${ri}-length-input`} value={r.length} onChange={(e) => updateRow(i, ri, 'length', e.target.value)} className="h-8 text-[12px]" placeholder='e.g. 26&quot;' /></div>
                    <div><Label className="text-[10px]">Shoulder</Label><Input data-testid={`size-chart-${i}-row-${ri}-shoulder-input`} value={r.shoulder} onChange={(e) => updateRow(i, ri, 'shoulder', e.target.value)} className="h-8 text-[12px]" placeholder='e.g. 17&quot;' /></div>
                    <button data-testid={`size-chart-${i}-row-${ri}-remove-button`} aria-label={`Remove ${r.size}`} onClick={() => removeRow(i, ri)} className="h-8 text-red-500 grid place-items-center"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                {(sc.rows || []).length === 0 && <p className="text-[11px] text-neutral-400">No rows yet. Click Row to add one.</p>}
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => del(i)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>
              <Button data-testid={`size-chart-${i}-save-button`} size="sm" onClick={() => save(i)} disabled={busy === (sc.id || `new-${i}`)} className="bg-orange-500 hover:bg-orange-600">
                {busy === (sc.id || `new-${i}`) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" />Save</>}
              </Button>
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="text-[13px] text-neutral-500 text-center py-4">No size charts yet. Click Add Size Chart.</p>}
      </div>
    </SectionCard>
  );
};

const ProductsEditor = ({ productsList, categoriesList, sizeChartsList, refresh }) => {
  const [list, setList] = useState(productsList || []);
  const [busy, setBusy] = useState(null);
  useEffect(() => setList(productsList || []), [productsList]);

  const update = (i, k, v) => { const next = [...list]; next[i] = { ...next[i], [k]: v }; setList(next); };

  const updateColor = (i, ci, k, v) => {
    const next = [...list]; const colors = [...(next[i].colors || [])];
    colors[ci] = { ...colors[ci], [k]: v }; next[i] = { ...next[i], colors }; setList(next);
  };
  const addColor = (i) => { const next = [...list]; next[i] = { ...next[i], colors: [...(next[i].colors || []), { name: 'New', hex: '#000000', stock_status: 'ready', moq: 1, moq_step: 1 }] }; setList(next); };
  const removeColor = (i, ci) => { const next = [...list]; next[i] = { ...next[i], colors: next[i].colors.filter((_, x) => x !== ci) }; setList(next); };

  const setSizes = (i, v) => update(i, 'sizes', v.split(',').map((s) => s.trim()).filter(Boolean));
  const setPhotos = (i, photos) => update(i, 'photos', photos);
  const addPhoto = async (i, file) => {
    const url = await uploadImage(file);
    setPhotos(i, [...(list[i].photos || []), url]);
  };
  const removePhoto = (i, pi) => setPhotos(i, list[i].photos.filter((_, x) => x !== pi));

  const save = async (i) => {
    const item = list[i]; setBusy(item.id || `new-${i}`);
    try {
      if (item.id) await products.update(item.id, item);
      else await products.create(item);
      toast.success('Saved'); await refresh();
    } catch { toast.error('Save failed'); } finally { setBusy(null); }
  };

  const del = async (i) => {
    const item = list[i]; if (!confirm('Delete product?')) return;
    if (item.id) { setBusy(item.id); try { await products.remove(item.id); await refresh(); toast.success('Deleted'); } catch { toast.error('Failed'); } finally { setBusy(null); } }
    else setList(list.filter((_, j) => j !== i));
  };

  const add = () => setList([...list, { category_slug: categoriesList?.[0]?.slug || 'oversize', name: 'New Product', description: '', fabric: '', gsm: '', price_high: 200, price_low: 240, min_qty: 10, sizes: ['S','M','L'], colors: [], photos: [], order: list.length }]);

  const duplicate = (i) => {
    const src = list[i];
    const { id, ...rest } = src;
    const copy = {
      ...JSON.parse(JSON.stringify(rest)),
      name: `${src.name || 'Product'} (Copy)`,
      order: list.length,
    };
    const next = [...list];
    next.splice(i + 1, 0, copy);
    setList(next);
    toast.success('Duplicated — edit & Save');
  };

  return (
    <SectionCard title="Products" action={<Button size="sm" onClick={add} variant="outline"><Plus className="w-3.5 h-3.5 mr-1" />Add Product</Button>}>
      <div className="space-y-4">
        {list.map((p, i) => (
          <div key={p.id || `new-${i}`} className="border border-neutral-200 rounded-lg p-3 bg-neutral-50">
            <div className="grid md:grid-cols-2 gap-2 mb-2">
              <div><Label className="text-[11px]">Name</Label><Input value={p.name || ''} onChange={(e) => update(i, 'name', e.target.value)} /></div>
              <div>
                <Label className="text-[11px]">Category</Label>
                <select value={p.category_slug || ''} onChange={(e) => update(i, 'category_slug', e.target.value)} className="w-full h-9 px-2 rounded border border-neutral-300 text-[13px] bg-white">
                  {(categoriesList || []).map((c) => <option key={c.id} value={c.slug}>{c.label}</option>)}
                </select>
              </div>
              <div className="md:col-span-2"><Label className="text-[11px]">Description</Label><Textarea rows={2} value={p.description || ''} onChange={(e) => update(i, 'description', e.target.value)} /></div>
              <div className="md:col-span-2"><Label className="text-[11px]">Fabric</Label><Input value={p.fabric || ''} onChange={(e) => update(i, 'fabric', e.target.value)} /></div>
              <div><Label className="text-[11px]">Price (bulk)</Label><Input type="number" value={p.price_high || 0} onChange={(e) => update(i, 'price_high', parseInt(e.target.value) || 0)} /></div>
              <div><Label className="text-[11px]">Price (sample)</Label><Input type="number" value={p.price_low || 0} onChange={(e) => update(i, 'price_low', parseInt(e.target.value) || 0)} /></div>
              <div><Label className="text-[11px]">Bulk price starts at qty (pricing only)</Label><Input data-testid={`product-${i}-bulk-price-quantity-input`} type="number" value={p.min_qty || 1} onChange={(e) => update(i, 'min_qty', parseInt(e.target.value) || 1)} /></div>
              <div><Label className="text-[11px]">Sizes (comma separated)</Label><Input value={(p.sizes || []).join(', ')} onChange={(e) => setSizes(i, e.target.value)} /></div>
              <div>
                <Label className="text-[11px]">Size Chart</Label>
                <select data-testid={`product-${i}-size-chart-select`} value={p.size_chart_id || ''} onChange={(e) => update(i, 'size_chart_id', e.target.value)} className="w-full h-9 px-2 rounded border border-neutral-300 text-[13px] bg-white">
                  <option value="">— Default chart —</option>
                  {(sizeChartsList || []).map((sc) => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-[11px]">GSM (category ke GSM filter ke liye — blank = sabme dikhega)</Label>
                {(() => {
                  const cat = (categoriesList || []).find((c) => c.slug === p.category_slug);
                  const opts = cat?.gsm_options || [];
                  return opts.length ? (
                    <select data-testid={`product-${i}-gsm-select`} value={p.gsm || ''} onChange={(e) => update(i, 'gsm', e.target.value)} className="w-full h-9 px-2 rounded border border-neutral-300 text-[13px] bg-white">
                      <option value="">— Sabhi GSM me dikhaayein —</option>
                      {opts.map((g) => <option key={g} value={g}>{g} GSM</option>)}
                    </select>
                  ) : (
                    <Input data-testid={`product-${i}-gsm-input`} value={p.gsm || ''} onChange={(e) => update(i, 'gsm', e.target.value)} placeholder="e.g. 240 (optional)" />
                  );
                })()}
              </div>
            </div>

            <div className="mb-2">
              <div className="flex items-center justify-between mb-1"><Label className="text-[11px]">Colours — har colour ka stock, MOQ aur step required hai</Label><Button data-testid={`product-${i}-add-colour-button`} size="sm" variant="outline" onClick={() => addColor(i)} className="h-6 text-[11px]"><Plus className="w-3 h-3 mr-0.5" />Colour</Button></div>
              <div className="space-y-2">
                {(p.colors || []).map((c, ci) => (
                  <div key={ci} data-testid={`product-${i}-colour-${ci}-editor`} className="grid grid-cols-[36px,1fr] md:grid-cols-[36px,minmax(120px,1fr),170px,100px,100px,32px] items-end gap-2 bg-white border rounded p-2">
                    <input data-testid={`product-${i}-colour-${ci}-hex-input`} aria-label={`${c.name} colour`} type="color" value={c.hex} onChange={(e) => updateColor(i, ci, 'hex', e.target.value)} className="w-9 h-9 rounded cursor-pointer" />
                    <div><Label className="text-[10px]">Colour name</Label><Input data-testid={`product-${i}-colour-${ci}-name-input`} value={c.name} onChange={(e) => updateColor(i, ci, 'name', e.target.value)} className="h-8 text-[12px]" /></div>
                    <div className="col-span-2 md:col-span-1">
                      <Label className="text-[10px]">Stock type</Label>
                      <select data-testid={`product-${i}-colour-${ci}-stock-select`} value={c.stock_status === 'made_to_order' ? 'made_to_order' : 'ready'} onChange={(e) => updateColor(i, ci, 'stock_status', e.target.value)} className="w-full h-8 px-2 rounded border border-neutral-300 text-[12px] bg-white">
                        <option value="ready">Ready Stock</option>
                        <option value="made_to_order">Made to Order</option>
                      </select>
                    </div>
                    <div><Label className="text-[10px]">Colour MOQ</Label><Input data-testid={`product-${i}-colour-${ci}-moq-input`} min="1" type="number" value={c.moq || 1} onChange={(e) => updateColor(i, ci, 'moq', Math.max(1, parseInt(e.target.value) || 1))} className="h-8 text-[12px]" /></div>
                    <div><Label className="text-[10px]">Step MOQ</Label><Input data-testid={`product-${i}-colour-${ci}-step-input`} min="1" type="number" value={c.moq_step || 1} onChange={(e) => updateColor(i, ci, 'moq_step', Math.max(1, parseInt(e.target.value) || 1))} className="h-8 text-[12px]" /></div>
                    <button data-testid={`product-${i}-colour-${ci}-remove-button`} aria-label={`Remove ${c.name}`} onClick={() => removeColor(i, ci)} className="h-8 text-red-500 grid place-items-center"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-2">
              <Label className="text-[11px]">Photos</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {(p.photos || []).map((ph, pi) => (
                  <div key={pi} className="relative w-16 h-16 rounded overflow-hidden border">
                    <img src={ph} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => removePhoto(i, pi)} className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full w-4 h-4 grid place-items-center"><X className="w-2.5 h-2.5" /></button>
                  </div>
                ))}
                <label className="w-16 h-16 border-2 border-dashed border-neutral-300 rounded grid place-items-center cursor-pointer hover:border-orange-500 text-neutral-400 hover:text-orange-500">
                  <Upload className="w-4 h-4" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) addPhoto(i, f); e.target.value = ''; }} />
                </label>
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <Button data-testid={`product-${i}-duplicate-button`} size="sm" variant="outline" onClick={() => duplicate(i)} className="text-sky-700 hover:bg-sky-50"><Copy className="w-3.5 h-3.5 mr-1" />Duplicate</Button>
              <Button size="sm" variant="outline" onClick={() => del(i)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>
              <Button size="sm" onClick={() => save(i)} disabled={busy === (p.id || `new-${i}`)} className="bg-orange-500 hover:bg-orange-600">
                {busy === (p.id || `new-${i}`) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" />Save</>}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
};

const CustomersManager = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try { setList(await customers.list()); } catch { toast.error('Customers load failed'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = list.filter((c) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [c.name, c.email, c.phone, c.alternate_phone, c.pincode, c.gst_number].some((v) => (v || '').toLowerCase().includes(q));
  });

  return (
    <SectionCard
      title={`Customers (${list.length})`}
      action={<Button data-testid="customers-refresh-button" size="sm" variant="outline" onClick={load} disabled={loading}><RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>}
    >
      <Input data-testid="customers-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, phone, email, pincode or GST…" className="mb-3 h-9 text-[13px]" />
      {loading ? (
        <div className="py-10 text-center text-neutral-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-10 text-center text-[13px] text-neutral-500"><User className="w-8 h-8 mx-auto mb-2 text-neutral-300" />No customers yet.</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((c) => (
            <div key={c.id} data-testid={`customer-card-${c.id}`} className="border border-neutral-200 rounded-lg p-3 bg-white">
              <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                <span className="font-semibold text-[14px] text-neutral-900 flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-orange-500" />{c.name || '—'}</span>
                <span className="text-[11px] text-neutral-400">{fmtDate(c.created_at)}</span>
              </div>
              <div className="grid md:grid-cols-2 gap-x-4 gap-y-1 text-[12px] text-neutral-700">
                <div><span className="text-neutral-500">Phone:</span> <a href={`tel:${c.phone}`} className="text-blue-700">{c.phone || '—'}</a></div>
                <div><span className="text-neutral-500">Alt phone:</span> {c.alternate_phone || '—'}</div>
                <div><span className="text-neutral-500">Email:</span> {c.email || '—'}</div>
                <div><span className="text-neutral-500">Pincode:</span> {c.pincode || '—'}</div>
                <div className="md:col-span-2"><span className="text-neutral-500">Address:</span> {c.address || '—'}</div>
                <div><span className="text-neutral-500">GST:</span> {c.gst_number || '—'}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
};

const CategoriesEditor = ({ list: cats, refresh }) => {
  const [list, setList] = useState(cats || []);
  const [busy, setBusy] = useState(null);
  const [gsmText, setGsmText] = useState({});
  useEffect(() => setList(cats || []), [cats]);

  const update = (i, k, v) => setList((prev) => { const next = [...prev]; next[i] = { ...next[i], [k]: v }; return next; });

  const onGsmChange = (i, raw) => {
    setGsmText((prev) => ({ ...prev, [i]: raw }));
    const arr = raw.split(',').map((s) => s.trim()).filter(Boolean);
    setList((prev) => { const next = [...prev]; next[i] = { ...next[i], gsm_options: arr, has_gsm: arr.length > 0 }; return next; });
  };
  const gsmValue = (i, c) => (gsmText[i] !== undefined ? gsmText[i] : (c.gsm_options || []).join(', '));

  const save = async (i) => {
    const item = list[i]; setBusy(item.id || `new-${i}`);
    try {
      if (item.id) await categories.update(item.id, item); else await categories.create(item);
      setGsmText((prev) => { const n = { ...prev }; delete n[i]; return n; });
      toast.success('Saved'); await refresh();
    } catch { toast.error('Failed'); } finally { setBusy(null); }
  };
  const del = async (i) => {
    const item = list[i]; if (!confirm('Delete category?')) return;
    if (item.id) { setBusy(item.id); try { await categories.remove(item.id); await refresh(); toast.success('Deleted'); } catch { toast.error('Failed'); } finally { setBusy(null); } }
    else setList(list.filter((_, j) => j !== i));
  };
  const add = () => setList([...list, { slug: `cat-${Date.now()}`, label: 'New Category', has_gsm: false, gsm_options: [], hot: false, order: list.length }]);

  return (
    <SectionCard title="Categories" action={<Button size="sm" onClick={add} variant="outline"><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>}>
      <div className="space-y-2">
        {list.map((c, i) => (
          <div key={c.id || `new-${i}`} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end bg-neutral-50 p-2 rounded border border-neutral-200">
            <div className="md:col-span-3"><Label className="text-[11px]">Label</Label><Input value={c.label || ''} onChange={(e) => update(i, 'label', e.target.value)} /></div>
            <div className="md:col-span-3"><Label className="text-[11px]">Slug (URL id)</Label><Input value={c.slug || ''} onChange={(e) => update(i, 'slug', e.target.value)} /></div>
            <div className="md:col-span-3"><Label className="text-[11px]">GSM options (comma)</Label><Input data-testid={`category-${i}-gsm-options-input`} value={gsmValue(i, c)} onChange={(e) => onGsmChange(i, e.target.value)} placeholder="e.g. 180, 210, 240" /></div>
            <div className="md:col-span-1 flex items-center gap-1"><input type="checkbox" checked={c.hot || false} onChange={(e) => update(i, 'hot', e.target.checked)} /><span className="text-[11px]">Hot</span></div>
            <div className="md:col-span-2 flex gap-1">
              <Button size="sm" onClick={() => save(i)} className="bg-orange-500 hover:bg-orange-600 flex-1">{busy === (c.id || `new-${i}`) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}</Button>
              <Button size="sm" variant="outline" onClick={() => del(i)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
};

const LayoutEditor = ({ settings, onSave }) => {
  const [form, setForm] = useState(settings || { product_layout: 'detail', show_gsm_switcher: true, show_size_chart_tab: true, show_order_summary: true, show_live_badge: true, sections: [] });
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(settings || form), [settings]);

  const save = async () => {
    setSaving(true);
    try { await pageSettingsApi.update(form); toast.success('Layout saved'); onSave(); }
    catch { toast.error('Save failed'); } finally { setSaving(false); }
  };

  const setField = (k, v) => setForm({ ...form, [k]: v });

  const moveSection = (idx, dir) => {
    const arr = [...(form.sections || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    arr.forEach((s, i) => (s.order = i));
    setField('sections', arr);
  };

  const toggleVisible = (idx) => {
    const arr = [...(form.sections || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    arr[idx] = { ...arr[idx], visible: !arr[idx].visible };
    setField('sections', arr);
  };

  const setSectionTitle = (idx, val) => {
    const arr = [...(form.sections || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    arr[idx] = { ...arr[idx], title: val };
    setField('sections', arr);
  };

  const layouts = [
    { key: 'detail', label: 'Detail View', desc: 'Full product with color × size quantity matrix (default)' },
    { key: 'grid', label: 'Grid View', desc: 'Product cards in a grid — better for browsing many products' },
    { key: 'list', label: 'List View', desc: 'Compact horizontal rows — good for quick scanning' },
  ];

  const sortedSections = [...(form.sections || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <>
      <SectionCard title="Product Page Layout" action={<Button size="sm" onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600"><Save className="w-3.5 h-3.5 mr-1" />Save</Button>}>
        <div className="grid md:grid-cols-3 gap-3">
          {layouts.map((l) => {
            const active = form.product_layout === l.key;
            return (
              <button
                key={l.key}
                onClick={() => setField('product_layout', l.key)}
                className={`text-left p-3 rounded-lg border-2 transition-all ${active ? 'border-orange-500 bg-orange-50' : 'border-neutral-200 bg-white hover:border-neutral-400'}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-4 h-4 rounded-full border-2 ${active ? 'border-orange-500 bg-orange-500' : 'border-neutral-300'}`}>
                    {active && <div className="w-1.5 h-1.5 bg-white rounded-full m-auto mt-[3px]" />}
                  </div>
                  <span className="font-semibold text-[13px]">{l.label}</span>
                </div>
                <p className="text-[11px] text-neutral-600 leading-snug">{l.desc}</p>
              </button>
            );
          })}
        </div>
        <div className="mt-4 pt-3 border-t border-neutral-200 grid grid-cols-2 md:grid-cols-4 gap-2">
          <label className="flex items-center gap-2 text-[12px] cursor-pointer">
            <input type="checkbox" checked={form.show_order_summary} onChange={(e) => setField('show_order_summary', e.target.checked)} className="accent-orange-500" />
            Order Summary sidebar
          </label>
          <label className="flex items-center gap-2 text-[12px] cursor-pointer">
            <input type="checkbox" checked={form.show_size_chart_tab} onChange={(e) => setField('show_size_chart_tab', e.target.checked)} className="accent-orange-500" />
            Size Chart tab
          </label>
          <label className="flex items-center gap-2 text-[12px] cursor-pointer">
            <input type="checkbox" checked={form.show_gsm_switcher} onChange={(e) => setField('show_gsm_switcher', e.target.checked)} className="accent-orange-500" />
            GSM switcher
          </label>
          <label className="flex items-center gap-2 text-[12px] cursor-pointer">
            <input type="checkbox" checked={form.show_live_badge} onChange={(e) => setField('show_live_badge', e.target.checked)} className="accent-orange-500" />
            Live badge (floating)
          </label>
        </div>
      </SectionCard>

      <SectionCard title="Homepage Sections (order, visibility, titles)" action={<Button size="sm" onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600"><Save className="w-3.5 h-3.5 mr-1" />Save</Button>}>
        <p className="text-[12px] text-neutral-500 mb-2">Use arrows to reorder. Toggle to show/hide. Titles edit karo aur Save dabao.</p>
        <div className="space-y-2">
          {sortedSections.map((s, i) => (
            <div key={s.key} className="flex items-center gap-2 bg-neutral-50 rounded border border-neutral-200 p-2">
              <div className="flex flex-col">
                <button onClick={() => moveSection(i, -1)} disabled={i === 0} className="w-6 h-5 grid place-items-center rounded hover:bg-neutral-200 disabled:opacity-30">▲</button>
                <button onClick={() => moveSection(i, 1)} disabled={i === sortedSections.length - 1} className="w-6 h-5 grid place-items-center rounded hover:bg-neutral-200 disabled:opacity-30">▼</button>
              </div>
              <div className="w-16 text-[11px] font-mono text-neutral-500 uppercase">{s.key}</div>
              <Input value={s.title} onChange={(e) => setSectionTitle(i, e.target.value)} className="flex-1 h-8 text-[13px]" placeholder="Section title" />
              <label className="flex items-center gap-1 text-[12px] cursor-pointer whitespace-nowrap">
                <input type="checkbox" checked={s.visible} onChange={() => toggleVisible(i)} className="accent-orange-500" />
                {s.visible ? 'Visible' : 'Hidden'}
              </label>
            </div>
          ))}
        </div>
      </SectionCard>
    </>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { data, refresh } = useSite();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    (async () => {
      const ok = await auth.verify();
      if (!ok) navigate('/admin/login');
      else setChecking(false);
    })();
  }, [navigate]);

  if (checking || !data) {
    return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>;
  }

  const logout = () => { auth.logout(); navigate('/admin/login'); };

  return (
    <div className="min-h-screen bg-neutral-50">
      <Toaster position="top-center" richColors />
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-orange-500 text-white grid place-items-center font-bold">A</div>
            <div>
              <div className="font-bold text-[15px]">Admin Panel</div>
              <div className="text-[11px] text-neutral-500">Edit your website content</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => window.open('/', '_blank')}><ExternalLink className="w-3.5 h-3.5 mr-1" />View Site</Button>
            <Button variant="outline" size="sm" onClick={logout} className="text-red-600"><LogOut className="w-3.5 h-3.5 mr-1" />Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4">
        <Tabs defaultValue="orders" className="w-full">
          <TabsList className="grid grid-cols-3 md:grid-cols-12 mb-4 h-auto">
            <TabsTrigger value="orders" data-testid="admin-tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="customers" data-testid="admin-tab-customers">Customers</TabsTrigger>
            <TabsTrigger value="layout">Layout</TabsTrigger>
            <TabsTrigger value="branding">Branding</TabsTrigger>
            <TabsTrigger value="nav">Nav</TabsTrigger>
            <TabsTrigger value="banners">Banners</TabsTrigger>
            <TabsTrigger value="categories">Categories</TabsTrigger>
            <TabsTrigger value="products">Products</TabsTrigger>
            <TabsTrigger value="size_charts" data-testid="admin-tab-size-charts">Size Charts</TabsTrigger>
            <TabsTrigger value="features">Features</TabsTrigger>
            <TabsTrigger value="pricing">Pricing</TabsTrigger>
            <TabsTrigger value="faqs">FAQs</TabsTrigger>
          </TabsList>

          <TabsContent value="orders">
            <OrdersManager />
          </TabsContent>

          <TabsContent value="customers">
            <CustomersManager />
          </TabsContent>

          <TabsContent value="layout">
            <LayoutEditor settings={data.page_settings} onSave={refresh} />
          </TabsContent>

          <TabsContent value="branding">
            <BrandingEditor data={data.branding} onSave={refresh} />
          </TabsContent>
          <TabsContent value="nav">
            <SimpleListEditor title="Navigation Links / Buttons" items={data.nav_links} api={navLinks} refresh={refresh}
              defaultNew={{ label: 'New Button', href: '#', product_id: '', order: 0 }}
              fields={[
                { key: 'label', label: 'Button text', cls: 'md:col-span-3' },
                { key: 'product_id', label: 'Open this product on click', cls: 'md:col-span-4', type: 'select', testId: 'nav-product-select',
                  options: [{ value: '', label: '— None (use link below) —' }, ...(data.products || []).map((p) => ({ value: p.id, label: p.name }))] },
                { key: 'href', label: 'Link (used if no product)', cls: 'md:col-span-3' },
                { key: 'order', label: 'Order', type: 'number', cls: 'md:col-span-1' },
              ]} />
          </TabsContent>
          <TabsContent value="banners">
            <SimpleListEditor title="Top Promo Banners" items={data.banners} api={banners} refresh={refresh}
              defaultNew={{ label: 'New Promo', icon: 'Star', order: 0 }}
              fields={[
                { key: 'label', label: 'Text', cls: 'md:col-span-6' },
                { key: 'icon', label: 'Icon (Star/IndianRupee/MessageCircle)', cls: 'md:col-span-3' },
                { key: 'order', label: 'Order', type: 'number', cls: 'md:col-span-1' },
              ]} />
          </TabsContent>
          <TabsContent value="categories">
            <CategoriesEditor list={data.categories} refresh={refresh} />
          </TabsContent>
          <TabsContent value="products">
            <ProductsEditor productsList={data.products} categoriesList={data.categories} sizeChartsList={data.size_charts} refresh={refresh} />
          </TabsContent>
          <TabsContent value="size_charts">
            <SizeChartsEditor list={data.size_charts} refresh={refresh} />
          </TabsContent>
          <TabsContent value="features">
            <SimpleListEditor title="Feature Cards" items={data.features} api={features} refresh={refresh}
              defaultNew={{ icon: 'Zap', title: 'New Feature', desc: 'Description', order: 0 }}
              fields={[
                { key: 'icon', label: 'Icon (Scissors/Ruler/Package/Zap/Palette/Truck/Factory/IndianRupee)', cls: 'md:col-span-3' },
                { key: 'title', label: 'Title', cls: 'md:col-span-3' },
                { key: 'desc', label: 'Description', cls: 'md:col-span-4' },
              ]} />
            <SimpleListEditor title="Stats Strip" items={data.stats} api={stats} refresh={refresh}
              defaultNew={{ value: 'New', label: 'Description', order: 0 }}
              fields={[
                { key: 'value', label: 'Value (big text)', cls: 'md:col-span-4' },
                { key: 'label', label: 'Label', cls: 'md:col-span-6' },
              ]} />
          </TabsContent>
          <TabsContent value="pricing">
            <SimpleListEditor title="Pricing Table" items={data.pricing} api={pricing} refresh={refresh}
              defaultNew={{ product: 'New Product', size: 'S to XL', price_high: 200, price_low: 240, order: 0 }}
              fields={[
                { key: 'product', label: 'Product', cls: 'md:col-span-3' },
                { key: 'size', label: 'Size range', cls: 'md:col-span-3' },
                { key: 'price_high', label: 'Bulk price', type: 'number', cls: 'md:col-span-2' },
                { key: 'price_low', label: 'Sample price', type: 'number', cls: 'md:col-span-2' },
              ]} />
          </TabsContent>
          <TabsContent value="faqs">
            <SimpleListEditor title="FAQs" items={data.faqs} api={faqs} refresh={refresh}
              defaultNew={{ q: 'New question?', a: 'Answer here', order: 0 }}
              fields={[
                { key: 'q', label: 'Question', cls: 'md:col-span-4' },
                { key: 'a', label: 'Answer', type: 'textarea', cls: 'md:col-span-6' },
              ]} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
